import type { ColumnDef } from '@tanstack/react-table';
import { Icon } from 'components/icon';
import {
  LeaderExtendQuery,
  TransactioneElecStatus,
  TransactionStatusColor,
  TransactionStatusEnum,
  TransactionTypes,
} from 'configs/constants';
import { subMonths, endOfDay, startOfDay } from 'date-fns';
import { format } from 'date-fns';
import { Button, NumberUtils } from 'modules';
import React from 'react';
import { MainRouteKeys } from 'routes/enums';
import type { TransPayEntity } from 'services/openapi';
import { Roles } from 'configs/roles';
import { getUserInfo, checkValidRole } from 'utils/helpers';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_INDEX } from 'configs';
import { formatDateTime } from 'utils/helpers/helpers';

export type FilterType = {
  transactionCode?: string;
  orderCode?: string;
  status:
    | 'Thành công'
    | 'Thất bại'
    | 'Đã hủy'
    | 'Đang xử lý'
    | 'Đang chờ hủy'
    | 'Tất cả';
  leader?: string;
  userExec?: string;
  serviceType?: string;
  fromDate?: string | number;
  toDate?: string | number;
  searchDetailContent?: string;
  refreshData: number;
  pageSize: number;
  pageIndex: number;
};

export const getInitFilterValues = (urlParams: any): FilterType => {
  return {
    transactionCode: urlParams?.get('transactionCode') ?? '',
    orderCode: urlParams?.get('orderCode') ?? '',
    status: urlParams?.get('status') ?? TransactioneElecStatus['Tất cả'],
    leader: urlParams?.get('leader') ?? LeaderExtendQuery['Tất cả'],
    userExec: urlParams?.get('userExec') ?? '',
    serviceType: urlParams?.get('serviceType') ?? TransactionTypes['Tất cả'],
    fromDate:
      urlParams?.get('fromDate') ??
      startOfDay(subMonths(new Date(), 1)).toISOString(),
    toDate: urlParams?.get('toDate') ?? endOfDay(new Date()).toISOString(),
    searchDetailContent: urlParams?.get('searchDetailContent') ?? '',
    refreshData: 0,
    pageSize: Number(urlParams?.get('pageSize') ?? DEFAULT_PAGE_SIZE),
    pageIndex: Number(urlParams?.get('pageIndex') ?? DEFAULT_PAGE_INDEX),
  };
};

// export const initFilterValues: FilterType = {
//   transactionCode: "",
//   orderCode: "",
//   status: TransactioneElecStatus["Tất cả"],
//   leader: LeaderExtendQuery["Tất cả"],
//   userExec: "",
//   serviceType: TransactionTypes["Tất cả"],
//   fromDate: startOfDay(subMonths(new Date(), 1)).toISOString(),
//   toDate: endOfDay(new Date()).toISOString(),
//   searchDetailContent: "",
//   refreshData: 0
// };

export const columnsDefined = (
  onClickCallback: Function,
  onRefreshStatusCallBack: Function,
  isLoadingRefreshStatusCallback: boolean,
): ColumnDef<TransPayEntity>[] => {
  const userPhoneNumber = getUserInfo().phoneNumber.replace('+84', '0');
  const isAdmin = checkValidRole([Roles.SUPER_ADMIN]);

  return [
    {
      accessorKey: 'transactionCode',
      header: 'Mã giao dịch',
      cell: (info) => {
        return (
          <a href={`${MainRouteKeys.TransactionBills}/${info.getValue()}`}>
            {String(info.getValue())}
          </a>
        );
      },
    },
    {
      accessorKey: 'orderCode',
      header: 'Mã đơn hàng',
    },
    {
      accessorKey: 'execPhoneNumber',
      header: 'Tài khoản thực hiện',
    },
    {
      accessorKey: 'leaderPhoneNumber',
      header: 'Trưởng nhóm',
    },
    {
      accessorKey: 'serviceType',
      header: 'Loại dịch vụ',
    },
    {
      accessorKey: 'customerCode',
      header: 'Mã khách hàng',
    },
    {
      accessorKey: 'customerFullName',
      header: 'Tên khách hàng',
    },
    {
      accessorKey: 'period',
      header: 'Kỳ tháng',
      cell: (info) => {
        const period = info.getValue();
        const month = String(period)?.slice?.(4, 6) || '';
        const year = String(period)?.slice?.(0, 4) || '';
        return `${month}/${year}`;
      },
    },
    {
      accessorKey: 'amount',
      header: 'Số tiền',
      // cell: (info) => NumberUtils.toFormatNumber(Number(info.getValue())),
      cell: (info) => {
        return (
          <div className={'textInfo'}>
            {info.getValue()
              ? NumberUtils.toFormatNumber(Number(info.getValue()))
              : '0'}
          </div>
        );
      },
    },
    {
      accessorKey: 'paymentAmount',
      header: 'Thanh toán',
      // cell: (info) => NumberUtils.toFormatNumber(Number(info.getValue())),
      cell: (info) => {
        return (
          <div className={'textInfo'}>
            {info.getValue()
              ? NumberUtils.toFormatNumber(Number(info.getValue()))
              : '0'}
          </div>
        );
      },
    },
    {
      accessorKey: 'rollbackAmount',
      header: 'Hoàn trả',
      // cell: (info) => NumberUtils.toFormatNumber(Number(info.getValue())),
      cell: (info) => {
        return (
          <div className={'textInfo'}>
            {info.getValue()
              ? NumberUtils.toFormatNumber(Number(info.getValue()))
              : '0'}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Trạng thái',
      cell: (info) => (
        <div
          className={
            TransactionStatusColor[
              info.getValue() as keyof typeof TransactionStatusColor
            ]
          }
        >
          {String(info.getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'updatedAt',
      header: 'Thời gian',
      cell: (info) =>
        format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy'),
    },
    {
      id: 'action',
      header: 'Thao tác',
      cell: (info) => {
        const now = new Date();

        const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);

        const someTime = new Date(info?.row?.original?.updatedAt);

        return (
          <div className='d-flex justify-content-start'>
            <a
              href={`${MainRouteKeys.TransactionBills}/${info.row.original?.transactionCode}`}
            >
              <Button
                type='button'
                buttonType='success'
                className='mr0 pdr10 pdl10 pdt0 pdb0'
                icon={() => <Icon.Eye />}
                label={false}
              />
            </a>
            {/* {info.row.original?.status == TransactionStatusEnum["Đang xử lý"] && <Button onClick={() => onRefreshStatusCallBack(info.row.original?.transactionCode)} type="button" buttonType="primary" className="ml5 mr0 pdr10 pdl10 pdt10 pdb10" isLoading={isLoadingRefreshStatusCallback} icon={() => <Icon.Reload />} label={false} />} */}
            {someTime > fifteenMinutesAgo && (
              <>
                {info.row.original?.status ==
                TransactionStatusEnum['Đang chờ hủy']
                  ? isAdmin && (
                      <Button
                        onClick={() =>
                          onClickCallback(info.row.original?.transactionCode)
                        }
                        label='Duyệt yêu cầu hủy'
                        type='button'
                        buttonType='danger-outline'
                        className='mr0 ml10 pd5 textNoWrap'
                      />
                    )
                  : info.row.original?.status ==
                      TransactionStatusEnum['Thành công'] &&
                    userPhoneNumber == info.row.original?.execPhoneNumber && (
                      <Button
                        onClick={() =>
                          onClickCallback(info.row.original?.transactionCode)
                        }
                        label='Gửi yêu cầu hủy'
                        type='button'
                        buttonType='danger-outline'
                        className='mr0 ml10 pd5 textNoWrap'
                      />
                    )}
              </>
            )}
          </div>
        );
      },
    },
  ];
};

export const convertVariables = (variables: FilterType) => {
  const { fromDate, toDate, leader, searchDetailContent, ...rest } = variables;

  const newDateFrom =
    typeof fromDate === 'string' ? new Date(fromDate).getTime() : fromDate;
  const newDateTo =
    typeof toDate === 'string' ? new Date(toDate).getTime() : toDate;

  return {
    ...rest,
    // ...(leader && leader === LeaderExtendQuery["Tất cả"] ? { leader: "" } : { leader: leader?.replace("-", "_") }),
    ...(leader && leader === LeaderExtendQuery['Tất cả']
      ? { leader: '' }
      : { leader }),
    fromDate: fromDate ? formatDateTime(Number(newDateFrom)) : '',
    toDate: toDate ? formatDateTime(Number(newDateTo), true) : '',
    ...(searchDetailContent ? { searchDetailContent } : {}),
  };
};

export const convertSelectStatusOptions = () => {
  return Object.keys(TransactioneElecStatus).map((key: string) => {
    return {
      label: TransactioneElecStatus[key as keyof typeof TransactioneElecStatus],
      value: key,
    };
  });
};

export const convertSelectTransactionTypeOptions = () => {
  return Object.keys(TransactionTypes).map((key: string) => {
    return {
      label: TransactionTypes[key as keyof typeof TransactionTypes],
      value: key,
    };
  });
};

export const convertSelectLeaderOptions = (data: any[]) => {
  if (!data || !data.length) return [];

  const all = Object.keys(LeaderExtendQuery).map((key: string) => {
    return {
      label: LeaderExtendQuery[key as keyof typeof LeaderExtendQuery],
      value: key,
    };
  });

  const leaders = data?.map((item) => {
    const newPhoneNo = item?.phoneNumber?.replace('+84', '0');

    return {
      label: `${item?.fullName} - ${newPhoneNo}`,
      value: `${item?.fullName}-${item?.phoneNumber}`,
    };
  });

  return [...all, ...leaders];
};

// export const exportHeaders = [
//   "Mã giao dịch",
//   "Mã hóa đơn",
//   "Loại hóa đơn",
//   "ĐL thanh toán",
//   "Trưởng nhóm",
//   "Quản lý",
//   "Tên sản phẩm",
//   "Mã khách hàng",
//   "Tên khách hàng",
//   "Địa chỉ khách hàng",
//   "Đợt",
//   "Tháng",
//   "Số tiền",
//   "Trạng thái",
//   "Mô tả",
//   "Thời gian",
//   "Mã ĐL",
// ];
